import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

// Overlay BLOQUEANTE para empresa/montadora enquanto houver pendências de montadores
// sobre clientes. Só libera após responder. Montadora pode devolver a montagem à distribuição.
export default function CentralPendenciasGate() {
  const { user } = useAuth();
  const location = useLocation();
  const [pendencias, setPendencias] = useState([]);
  const [resposta, setResposta] = useState({});
  const lastFetch = React.useRef(0);

  useEffect(() => {
    if (!user || !["empresa", "montadora"].includes(user.role)) return;
    if (Date.now() - lastFetch.current < 45000) return;
    lastFetch.current = Date.now();
    api.get("/central/pendencias").then(r => setPendencias(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [user, location.pathname]);

  const responder = async (p) => {
    const txt = (resposta[p.id] || "").trim();
    if (!txt) { toast.error("Escreva a resposta ao montador"); return; }
    try {
      await api.post(`/central/pendencias/${p.id}/responder`, { resposta: txt });
      setPendencias(prev => prev.filter(x => x.id !== p.id));
      toast.success("Resposta enviada ao montador!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const devolver = async (p) => {
    if (!window.confirm(`Devolver o atendimento ${p.pim_code} ao painel de distribuição? O montador atual será removido.`)) return;
    try {
      await api.post(`/montadora/servicos/${p.servico_id}/devolver-distribuicao`);
      await api.post(`/central/pendencias/${p.id}/responder`, { resposta: "Atendimento devolvido à distribuição pela central." });
      setPendencias(prev => prev.filter(x => x.id !== p.id));
      toast.success("Montagem devolvida à distribuição.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user || !["empresa", "montadora"].includes(user.role) || pendencias.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9100] bg-[#0A174A]/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="central-pendencias-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 max-h-[85vh] overflow-auto">
        <div className="flex items-center gap-2 text-aj-blue-deep font-display font-extrabold text-lg">
          <AlertTriangle className="text-[#DC2626]" size={20} /> Montador aguardando a central
        </div>
        <p className="text-sm text-aj-text-muted mt-1">
          Um montador precisa de ajuda com um cliente. Responda para liberar o painel — ele nunca pode ficar sem informação.
        </p>
        <div className="mt-4 space-y-4">
          {pendencias.map(p => (
            <div key={p.id} className="border border-[#F5C518] bg-[#FFFBEB] rounded-lg p-4" data-testid={`pendencia-${p.id}`}>
              <div className="text-xs font-bold text-aj-blue-deep">
                {p.pim_code} · Cliente {p.cliente_nome} · Montador {p.montador_nome}
              </div>
              <p className="text-sm mt-1 text-aj-text">"{p.texto}"</p>
              <textarea value={resposta[p.id] || ""} onChange={e => setResposta(s => ({ ...s, [p.id]: e.target.value }))}
                placeholder="Escreva a orientação ao montador…" rows={2}
                className="w-full mt-2 border border-[#E2E8F0] rounded-md p-2 text-sm"
                data-testid={`pendencia-resposta-${p.id}`} />
              <div className="flex gap-2 mt-2">
                <button onClick={() => responder(p)}
                  className="px-3 py-1.5 rounded-md bg-[#FF6700] hover:bg-[#e05a00] text-white text-xs font-bold"
                  data-testid={`pendencia-responder-${p.id}`}>
                  Responder e liberar
                </button>
                {user.role === "montadora" && (
                  <button onClick={() => devolver(p)}
                    className="px-3 py-1.5 rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-red-50 text-xs font-bold"
                    data-testid={`pendencia-devolver-${p.id}`}>
                    Devolver à distribuição
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
